// frontend/src/pages/Auth/Register.tsx

import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useAuth } from '@/hooks/useAuth'
import { Button } from '@/components/common/UI/Button'
import { Input } from '@/components/common/UI/Input'
import { Card } from '@/components/common/UI/Card'
import { SocialLoginButtons } from '@/components/auth/SocialLoginButtons'

// ============================================
// Схема валидации
// ============================================

const registerSchema = z.object({
  first_name: z.string().min(1, 'Имя обязательно').max(100),
  last_name: z.string().min(1, 'Фамилия обязательна').max(100),
  email: z.string().email('Введите корректный email'),
  password: z
    .string()
    .min(8, 'Пароль должен содержать минимум 8 символов')
    .regex(/[A-Z]/, 'Пароль должен содержать хотя бы одну заглавную букву')
    .regex(/[a-z]/, 'Пароль должен содержать хотя бы одну строчную букву')
    .regex(/[0-9]/, 'Пароль должен содержать хотя бы одну цифру'),
  confirm_password: z.string(),
  role: z.enum(['candidate', 'employer']),
  agree: z.boolean().refine((val) => val === true, {
    message: 'Необходимо согласиться с условиями',
  }),
}).refine((data) => data.password === data.confirm_password, {
  message: 'Пароли не совпадают',
  path: ['confirm_password'],
})

type RegisterFormData = z.infer<typeof registerSchema>

const RegisterIcon: React.FC<{ name: 'user' | 'building' | 'mail' | 'lock' | 'check' | 'briefcase' | 'search' }> = ({ name }) => {
  const paths: Record<typeof name, React.ReactNode> = {
    user: <><circle cx="12" cy="8" r="4" /><path d="M5 21v-2a7 7 0 0 1 14 0v2" /></>,
    building: <><path d="M3 21h18" /><path d="M5 21V5l7-3 7 3v16" /><path d="M9 9h.01M15 9h.01M9 13h.01M15 13h.01M10 21v-4h4v4" /></>,
    mail: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></>,
    lock: <><rect x="4" y="10" width="16" height="11" rx="2" /><path d="M8 10V7a4 4 0 1 1 8 0v3" /></>,
    check: <><path d="m5 12 4 4L19 6" /></>,
    briefcase: <><rect x="3" y="7" width="18" height="14" rx="2" /><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M3 12h18M10 12v2h4v-2" /></>,
    search: <><circle cx="11" cy="11" r="7" /><path d="m20 20-4-4" /></>,
  }
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="h-5 w-5">{paths[name]}</svg>
}

// ============================================
// Компонент регистрации
// ============================================

const Register: React.FC = () => {
  const { register: registerUser, isLoading } = useAuth()
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      role: 'candidate',
      agree: false,
    },
  })

  const selectedRole = watch('role')

  const onSubmit = async (data: RegisterFormData) => {
    const { agree, ...registerData } = data
    
    await registerUser(registerData, '/dashboard')
  }

  return (
    <div className="min-h-[calc(100vh-200px)] bg-gradient-to-b from-primary-50/70 via-white to-white px-4 py-10 sm:py-14">
      <div className="mx-auto grid w-full max-w-5xl items-center gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:gap-14">
        <section className="hidden lg:block">
          <div className="mb-6 inline-flex items-center gap-3 rounded-2xl border border-white bg-white/80 px-4 py-3 shadow-sm">
            <img src="/lenjob-logo.svg" alt="" className="h-10 w-10" />
            <div><p className="font-bold text-secondary-900">Len<span className="text-primary-600">Job</span></p><p className="text-xs text-secondary-500">Работа начинается здесь</p></div>
          </div>
          <h1 className="max-w-md text-4xl font-bold leading-tight tracking-tight text-secondary-950">Найдите работу или сильных сотрудников</h1>
          <p className="mt-4 max-w-md text-lg leading-7 text-secondary-600">Создайте аккаунт LenJob — нужные инструменты будут под рукой.</p>
          <div className="mt-8 space-y-4">
            {[
              { icon: 'search' as const, title: 'Поиск по городам', text: 'Находите вакансии и кандидатов по подходящим условиям.' },
              { icon: 'briefcase' as const, title: 'Удобный профиль', text: 'Храните резюме, вакансии и отклики в одном месте.' },
              { icon: 'check' as const, title: 'Больше возможностей', text: 'Сохраняйте поиски и получайте подходящие обновления.' },
            ].map(item => <div key={item.title} className="flex gap-3"><span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white text-primary-700 shadow-sm"><RegisterIcon name={item.icon} /></span><div><p className="font-semibold text-secondary-900">{item.title}</p><p className="mt-0.5 text-sm text-secondary-600">{item.text}</p></div></div>)}
          </div>
        </section>

        <div className="mx-auto w-full max-w-lg">
        <div className="mb-6 text-center lg:hidden">
          <img src="/lenjob-logo.svg" alt="LenJob" className="mx-auto mb-3 h-12 w-12" />
          <h1 className="text-3xl font-bold text-secondary-900">Создание аккаунта</h1>
          <p className="mt-2 text-secondary-600">Зарегистрируйтесь, чтобы искать работу или сотрудников</p>
        </div>

        <Card className="rounded-2xl border border-secondary-100 p-6 shadow-xl shadow-secondary-900/5 sm:p-8">
          <div className="mb-6 hidden lg:block"><h2 className="text-2xl font-bold text-secondary-900">Создать аккаунт</h2><p className="mt-1 text-sm text-secondary-600">Это займёт всего пару минут</p></div>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <fieldset>
              <legend className="mb-2 block text-sm font-medium text-secondary-700">Я хочу *</legend>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: 'candidate' as const, title: 'Найти работу', caption: 'Я соискатель', icon: 'user' as const },
                  { value: 'employer' as const, title: 'Найти сотрудников', caption: 'Я работодатель', icon: 'building' as const },
                ].map(option => <label key={option.value} className={`relative flex cursor-pointer flex-col gap-2 rounded-xl border p-4 transition ${selectedRole === option.value ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-500/15' : 'border-secondary-200 bg-white hover:border-primary-300'}`}>
                  <input type="radio" value={option.value} className="sr-only" {...register('role')} />
                  <span className={`grid h-10 w-10 place-items-center rounded-xl ${selectedRole === option.value ? 'bg-primary-600 text-white' : 'bg-secondary-100 text-secondary-600'}`}><RegisterIcon name={option.icon} /></span>
                  <span><span className="block text-sm font-semibold text-secondary-900">{option.title}</span><span className="mt-0.5 block text-xs text-secondary-500">{option.caption}</span></span>
                  {selectedRole === option.value && <span className="absolute right-3 top-3 text-primary-600"><RegisterIcon name="check" /></span>}
                </label>)}
              </div>
              {errors.role && <p className="mt-1.5 text-sm text-error">{errors.role.message}</p>}
            </fieldset>
            {/* Имя */}
            <Input
              id="register-first-name"
              label="Имя *"
              placeholder="Иван"
              error={errors.first_name?.message}
              leftIcon={<RegisterIcon name="user" />}
              {...register('first_name')}
            />

            {/* Фамилия */}
            <Input
              id="register-last-name"
              label="Фамилия *"
              placeholder="Иванов"
              error={errors.last_name?.message}
              leftIcon={<RegisterIcon name="user" />}
              {...register('last_name')}
            />

            {/* Email */}
            <Input
              id="register-email"
              label="Email *"
              type="email"
              placeholder="you@example.com"
              error={errors.email?.message}
              leftIcon={<RegisterIcon name="mail" />}
              {...register('email')}
            />

            {/* Пароль */}
            <Input
              id="register-password"
              label="Пароль *"
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              error={errors.password?.message}
              helper="Минимум 8 символов, заглавная и строчная буквы, цифра"
              leftIcon={<RegisterIcon name="lock" />}
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-secondary-400 hover:text-secondary-600"
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  )}
                </button>
              }
              {...register('password')}
            />

            {/* Подтверждение пароля */}
            <Input
              id="register-confirm-password"
              label="Подтверждение пароля *"
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="••••••••"
              error={errors.confirm_password?.message}
              leftIcon={<RegisterIcon name="lock" />}
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="text-secondary-400 hover:text-secondary-600"
                >
                  {showConfirmPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  )}
                </button>
              }
              {...register('confirm_password')}
            />

            {/* Рекомендация для работодателя */}
            {selectedRole === 'employer' && (
              <div className="flex gap-3 rounded-xl border border-blue-100 bg-blue-50 p-3 text-sm text-blue-900">
                <span className="mt-0.5 text-blue-700"><RegisterIcon name="building" /></span><div><p className="font-medium">Для работодателей</p>
                <p className="mt-1 text-xs leading-5 text-blue-800">
                  После регистрации вы сможете создавать компании, публиковать вакансии
                  и просматривать резюме кандидатов.
                </p></div>
              </div>
            )}

            {/* Согласие */}
            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="agree"
                className="mt-1 w-4 h-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                {...register('agree')}
              />
              <label htmlFor="agree" className="text-sm text-secondary-600">
                Я соглашаюсь с{' '}
                <Link to="/terms" className="text-primary-600 hover:underline">
                  условиями использования
                </Link>
                {' '}и{' '}
                <Link to="/privacy" className="text-primary-600 hover:underline">
                  политикой конфиденциальности
                </Link>
              </label>
            </div>
            {errors.agree && (
              <p className="text-sm text-error">{errors.agree.message}</p>
            )}

            {/* Кнопка регистрации */}
            <Button type="submit" fullWidth isLoading={isLoading}>
              Зарегистрироваться
            </Button>
          </form>

          {/* Разделитель */}
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-secondary-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-white px-4 text-secondary-500">Или продолжить с</span>
              </div>
            </div>

            <SocialLoginButtons role={selectedRole} />
          </div>

          {/* Ссылка на вход */}
          <div className="mt-6 text-center">
            <p className="text-sm text-secondary-600">
              Уже есть аккаунт?{' '}
              <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
                Войти
              </Link>
            </p>
          </div>
        </Card>
        <p className="mt-4 text-center text-xs text-secondary-500">Продолжая регистрацию, вы сможете управлять профилем и настройками уведомлений в личном кабинете.</p>
        </div>
      </div>
    </div>
  )
}

export default Register
